// Sidebar footer's system line (BFF /health): build versions + a brief
// up/down sweep of the domain APIs the BFF composes over.
//
// i18n: not a component, so it holds translation KEYS (`admin` namespace,
// `systemStatus.*`) and resolves them through `adminT`, never English literals.
// `getFixedT(null, ns)` binds the NAMESPACE only, never a language, so the
// verdict is composed in whatever language the console is showing at the moment
// the /health read lands.

import i18next from 'i18next';

const adminT = i18next.getFixedT(null, 'admin');

/** One downstream API in the BFF's sweep. */
export interface IDomainHealth {
  key: string;
  ok: boolean;
}

/** #812 — the deployment's rehearsal flag + the visible label (empty when off). */
export interface IExerciseMode {
  enabled: boolean;
  label: string;
}

/** BFF /health response. `version`/`domains`/`exercise` are additive — an older BFF omits them. */
export interface ISystemHealth {
  status: string;
  service: string;
  version?: string;
  domains?: IDomainHealth[];
  exercise?: IExerciseMode;
}

export type SystemTone = 'ok' | 'degraded' | 'offline' | 'checking';

export interface ISystemSummary {
  tone: SystemTone;
  /** Terse footer label — the sidebar is 224px wide. */
  label: string;
  /** Tooltip body: one line per probed domain. Empty when there is no sweep. */
  detail: string;
}

/**
 * Fold the /health read into the footer's one-dot verdict:
 * unreachable BFF → offline; any probed domain down → degraded; else ok.
 */
export function summarizeSystemHealth(
  health: ISystemHealth | undefined,
  isError: boolean,
): ISystemSummary {
  if (isError) return { tone: 'offline', label: adminT('systemStatus.unreachable'), detail: '' };
  if (!health) return { tone: 'checking', label: adminT('systemStatus.checking'), detail: '' };

  const domains = health.domains ?? [];
  const down = domains.filter((d) => !d.ok);
  const detail = domains
    .map((d) =>
      adminT(d.ok ? 'systemStatus.domainUp' : 'systemStatus.domainDown', { domain: d.key }),
    )
    .join('\n');

  if (down.length > 0) {
    return {
      tone: 'degraded',
      label: adminT('systemStatus.degraded', { domains: down.map((d) => d.key).join(', ') }),
      detail,
    };
  }
  return { tone: 'ok', label: adminT('systemStatus.operational'), detail };
}

/** Build SHA injected by vite.config's `define`; guarded so environments
 *  without the define (e.g. Storybook's builder) degrade to 'dev'. */
export function uiVersion(): string {
  return typeof __UI_VERSION__ !== 'undefined' ? __UI_VERSION__ : 'dev';
}
